// Sugalarm — native speech-capture bridge (cycle 383).
//
// The single owner of access to the native `VoiceCapture` plugin. That
// plugin lives in the SHARED Android source set, so it is registered on the
// bridge in BOTH Android builds — the wall-mount tablet `kiosk` APK and the
// `standard` phone app — and NOWHERE else:
//
//   • iOS shell   → no such class, no mic entitlement, no usage description
//   • iPad        → same (it is the iOS shell)
//   • web / PWA   → no Capacitor bridge at all, and the browser bundle
//                   contains no Web Audio / media-capture call whatsoever,
//                   so a page load can never ask for a microphone.
//
// That last point is grep-enforceable and is checked every cycle: the app
// bundle must contain ZERO media-capture calls. (The lazily code-split
// `heic2any` chunk vendors an unrelated library that mentions one; it is
// pre-existing since cycle 87, is only fetched when a HEIC photo is picked,
// and is not on any voice path.)
//
// so `isAvailable()` is false on every surface except the two Android ones,
// and the SPA's voice layer is a permanent no-op elsewhere.
//
// Reached through the bridge-injected `window.Capacitor.Plugins` proxy —
// NEVER a bare `import("@capacitor/...")` (cycle 26/138/197). Mirrors
// `lib/battery.ts` / `lib/kioskVolume.ts`.
//
// TRANSCRIPT HYGIENE: transcripts cross this boundary and stop. Nothing in
// this module (or its callers) sends a transcript to the server, writes one
// to analytics, or logs one anywhere that leaves the device.

import { capacitorPlatform, isCapacitorNative } from "./capacitor";

/** The closed error-code set the native plugin emits. Mirrors
 *  `VoiceCapturePlugin`'s constants verbatim. */
export type VoiceErrorCode =
  | "no_match"
  | "timeout"
  | "audio"
  | "permission"
  | "recognizer_unavailable"
  | "busy";

export interface VoiceCaptureHandlers {
  onPartial?: (transcript: string) => void;
  onFinal?: (transcript: string) => void;
  onError?: (code: VoiceErrorCode) => void;
}

interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

interface VoiceCaptureProxy {
  isAvailable(): Promise<{ available: boolean }>;
  checkPermission(): Promise<{ granted: boolean }>;
  requestPermission(): Promise<{ granted: boolean }>;
  startListening(): Promise<void>;
  stopListening(): Promise<void>;
  addListener(
    event: "voicePartial" | "voiceFinal" | "voiceError",
    cb: (data: Record<string, unknown>) => void,
  ): Promise<PluginListenerHandle> | PluginListenerHandle;
}

/** Dev-gated warn. Security review correctly noted that this module's
 *  warnings were the one part of the voice layer NOT behind the dev flag,
 *  which made the cycle's "client debug logging is dev-gated" claim not
 *  literally true. They never carried a transcript — they log an error
 *  object only — but the claim should hold as written, so they are gated
 *  too. Read directly (not via the hook) to keep this module React-free. */
function devWarn(...args: unknown[]): void {
  try {
    if (localStorage.getItem("sugalarm_voice_dev") === "1") {
      console.warn(...args);
    }
  } catch {
    /* storage unavailable — stay silent */
  }
}

/** Resolve the natively-registered plugin proxy, or null on any surface
 *  where it isn't present. Gated on the Android platform as well as on the
 *  proxy's existence — belt and braces, so an iOS build that somehow
 *  registered a same-named plugin still can't reach this path. */
function plugin(): VoiceCaptureProxy | null {
  if (!isCapacitorNative()) return null;
  if (capacitorPlatform() !== "android") return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.VoiceCapture;
  return p != null ? (p as VoiceCaptureProxy) : null;
}

/** True iff the native VoiceCapture plugin is on the bridge — i.e. the SPA
 *  is running inside one of the two Android builds. Every voice code path
 *  gates on this first. */
export function isPluginPresent(): boolean {
  return plugin() != null;
}

/** True iff the device can actually recognize speech (the plugin is present
 *  AND the system reports a usable recognizer). False everywhere else,
 *  including on an Android device with no speech service. */
export async function isAvailable(): Promise<boolean> {
  const p = plugin();
  if (p == null) return false;
  try {
    const r = await p.isAvailable();
    return r?.available === true;
  } catch (err) {
    devWarn("[voiceCapture] isAvailable failed", err);
    return false;
  }
}

/** Read the RECORD_AUDIO grant WITHOUT prompting. Safe on every surface. */
export async function checkPermission(): Promise<boolean> {
  const p = plugin();
  if (p == null) return false;
  try {
    const r = await p.checkPermission();
    return r?.granted === true;
  } catch (err) {
    devWarn("[voiceCapture] checkPermission failed", err);
    return false;
  }
}

/**
 * Request RECORD_AUDIO — the ONLY call in the SPA that can raise the
 * Android mic dialog.
 *
 * Callers must reach this solely from the user's explicit trigger, and
 * never on the kiosk (a wall-mounted screen can't answer a prompt; there
 * the grant is pre-applied at provisioning). `useVoiceCommands` enforces
 * both rules — this module deliberately keeps the primitive dumb.
 */
export async function requestPermission(): Promise<boolean> {
  const p = plugin();
  if (p == null) return false;
  try {
    const r = await p.requestPermission();
    return r?.granted === true;
  } catch (err) {
    devWarn("[voiceCapture] requestPermission failed", err);
    return false;
  }
}

/** Begin one recognition session. Never prompts: a missing grant surfaces
 *  as a `permission` error event. */
export async function startListening(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.startListening();
  } catch (err) {
    devWarn("[voiceCapture] startListening failed", err);
  }
}

/** Abandon the current session with no result delivered. Idempotent. */
export async function stopListening(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.stopListening();
  } catch (err) {
    devWarn("[voiceCapture] stopListening failed", err);
  }
}

function asTranscript(data: Record<string, unknown>): string | null {
  const t = data?.transcript;
  return typeof t === "string" && t.trim().length > 0 ? t : null;
}

const KNOWN_CODES: ReadonlySet<string> = new Set<VoiceErrorCode>([
  "no_match",
  "timeout",
  "audio",
  "permission",
  "recognizer_unavailable",
  "busy",
]);

/**
 * Subscribe to the three native events; returns an unsubscribe.
 * No-op (returns a no-op cleanup) when the plugin isn't available.
 *
 * An unrecognized error code from a future native build is normalized to
 * `recognizer_unavailable` — the inert-degrade path — rather than being
 * passed through as an unmodelled value.
 */
export function subscribe(handlers: VoiceCaptureHandlers): () => void {
  const p = plugin();
  if (p == null) return () => {};

  const handles: PluginListenerHandle[] = [];
  let cancelled = false;

  const add = (
    event: "voicePartial" | "voiceFinal" | "voiceError",
    cb: (data: Record<string, unknown>) => void,
  ) => {
    try {
      Promise.resolve(p.addListener(event, cb))
        .then((h) => {
          if (cancelled) {
            try {
              void h?.remove?.();
            } catch {
              /* ignore */
            }
          } else if (h) {
            handles.push(h);
          }
        })
        .catch((err) =>
          devWarn(`[voiceCapture] addListener(${event}) failed`, err),
        );
    } catch (err) {
      devWarn(`[voiceCapture] subscribe(${event}) failed`, err);
    }
  };

  add("voicePartial", (d) => {
    const t = asTranscript(d);
    if (t != null) handlers.onPartial?.(t);
  });
  add("voiceFinal", (d) => {
    const t = asTranscript(d);
    if (t != null) handlers.onFinal?.(t);
  });
  add("voiceError", (d) => {
    const raw = typeof d?.code === "string" ? d.code : "";
    const code = (
      KNOWN_CODES.has(raw) ? raw : "recognizer_unavailable"
    ) as VoiceErrorCode;
    handlers.onError?.(code);
  });

  return () => {
    cancelled = true;
    for (const h of handles) {
      try {
        void h.remove();
      } catch {
        /* ignore */
      }
    }
    handles.length = 0;
  };
}
